// Centralized error reporting system for frontend
import { ApiError, ErrorCode } from './errorHandler';

// Error reporting configuration
interface ErrorReportingConfig {
  enabled: boolean;
  logToConsole: boolean;
  sendToAnalytics: boolean;
  sendToMonitoring: boolean;
  apiEndpoint?: string;
  apiKey?: string;
  environment: 'development' | 'staging' | 'production';
  userId?: string;
  sessionId: string;
}

// Error report structure
interface ErrorReport {
  // Core error info
  errorId: string;
  timestamp: string;
  errorCode: ErrorCode;
  message: string;
  userMessage: string;
  
  // Context
  userId?: string;
  sessionId: string;
  environment: string;
  
  // Technical details
  stack?: string;
  url: string;
  userAgent: string;
  
  // API context (if applicable)
  apiEndpoint?: string;
  httpStatus?: number;
  requestId?: string;
  
  // User context
  userAction?: string;
  componentName?: string;
  
  // Additional metadata
  metadata?: Record<string, any>;
}

// Analytics events for error patterns
interface ErrorAnalytics {
  errorCode: ErrorCode;
  count: number;
  firstSeen: string;
  lastSeen: string;
  affectedUsers: Set<string>;
  commonPatterns: string[];
}

class ErrorReportingService {
  private config: ErrorReportingConfig;
  private errorQueue: ErrorReport[] = [];
  private analytics: Map<ErrorCode, ErrorAnalytics> = new Map();
  private isOnline = navigator.onLine;

  constructor() {
    this.config = {
      enabled: true,
      logToConsole: import.meta.env.DEV,
      sendToAnalytics: true,
      sendToMonitoring: import.meta.env.PROD,
      environment: import.meta.env.MODE as 'development' | 'staging' | 'production',
      sessionId: this.generateSessionId(),
      apiEndpoint: import.meta.env.VITE_ERROR_REPORTING_ENDPOINT,
      apiKey: import.meta.env.VITE_ERROR_REPORTING_KEY
    };

    this.setupEventListeners();
    this.startPeriodicFlush();
  }

  /**
   * Report an error - main entry point
   */
  async reportError(
    error: ApiError | Error,
    context?: {
      userAction?: string;
      componentName?: string;
      apiEndpoint?: string;
      metadata?: Record<string, any>;
    }
  ): Promise<void> {
    if (!this.config.enabled) return;

    const report = this.createErrorReport(error, context);
    
    // Update analytics
    this.updateAnalytics(report);
    
    // Log to console in development
    if (this.config.logToConsole) {
      this.logToConsole(report);
    }
    
    // Queue for sending
    this.errorQueue.push(report);
    
    // Send immediately for critical errors
    if (this.isCriticalError(report)) {
      await this.flushQueue();
    }
  }

  /**
   * Report API errors specifically
   */
  async reportApiError(
    error: ApiError,
    endpoint: string,
    httpStatus?: number,
    requestId?: string,
    context?: Record<string, any>
  ): Promise<void> {
    await this.reportError(error, {
      apiEndpoint: endpoint,
      metadata: {
        httpStatus,
        requestId,
        ...context
      }
    });
  }

  /**
   * Report user action that led to error
   */
  async reportUserActionError(
    error: ApiError | Error,
    userAction: string,
    componentName: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.reportError(error, {
      userAction,
      componentName,
      metadata
    });
  }

  /**
   * Get error analytics summary
   */
  getErrorAnalytics(): Record<string, any> {
    const summary = {
      totalErrors: this.errorQueue.length,
      uniqueErrorTypes: this.analytics.size,
      sessionId: this.config.sessionId,
      environment: this.config.environment,
      topErrors: [] as any[]
    };

    // Get top 10 most frequent errors
    const sortedErrors = Array.from(this.analytics.entries())
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 10);

    summary.topErrors = sortedErrors.map(([errorCode, analytics]) => ({
      errorCode,
      count: analytics.count,
      affectedUsers: analytics.affectedUsers.size,
      firstSeen: analytics.firstSeen,
      lastSeen: analytics.lastSeen
    }));

    return summary;
  }

  /**
   * Configure error reporting
   */
  configure(config: Partial<ErrorReportingConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Set user context
   */
  setUserContext(userId: string, metadata?: Record<string, any>): void {
    this.config.userId = userId;
    if (metadata) {
      this.config = { ...this.config, ...metadata };
    }
  }

  /**
   * Manually flush error queue
   */
  async flushQueue(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    try {
      // Send to multiple destinations
      await Promise.allSettled([
        this.sendToAnalytics(errors),
        this.sendToMonitoring(errors),
        this.sendToBackend(errors)
      ]);
    } catch (error) {
      // If sending fails, put errors back in queue
      this.errorQueue.unshift(...errors);
      console.error('Failed to flush error queue:', error);
    }
  }

  private createErrorReport(
    error: ApiError | Error,
    context?: {
      userAction?: string;
      componentName?: string;
      apiEndpoint?: string;
      metadata?: Record<string, any>;
    }
  ): ErrorReport {
    const isApiError = error instanceof ApiError;
    
    return {
      errorId: this.generateErrorId(),
      timestamp: new Date().toISOString(),
      errorCode: isApiError ? error.errorCode : ErrorCode.UNKNOWN_ERROR,
      message: error.message,
      userMessage: isApiError ? error.getUserMessage() : error.message,
      
      userId: this.config.userId,
      sessionId: this.config.sessionId,
      environment: this.config.environment,
      
      stack: error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      
      apiEndpoint: context?.apiEndpoint,
      httpStatus: context?.metadata?.httpStatus,
      requestId: context?.metadata?.requestId,
      
      userAction: context?.userAction,
      componentName: context?.componentName,
      
      metadata: context?.metadata
    };
  }

  private updateAnalytics(report: ErrorReport): void {
    const existing = this.analytics.get(report.errorCode);
    
    if (existing) {
      existing.count++;
      existing.lastSeen = report.timestamp;
      if (report.userId) {
        existing.affectedUsers.add(report.userId);
      }
    } else {
      this.analytics.set(report.errorCode, {
        errorCode: report.errorCode,
        count: 1,
        firstSeen: report.timestamp,
        lastSeen: report.timestamp,
        affectedUsers: new Set(report.userId ? [report.userId] : []),
        commonPatterns: []
      });
    }
  }

  private logToConsole(report: ErrorReport): void {
    const style = this.isCriticalError(report) ? 'color: red; font-weight: bold;' : 'color: orange;';
    
    console.group(`%c🚨 Error Report: ${report.errorCode}`, style);
    console.log('Message:', report.userMessage);
    console.log('Timestamp:', report.timestamp);
    console.log('Component:', report.componentName || 'Unknown');
    console.log('User Action:', report.userAction || 'Unknown');
    if (report.apiEndpoint) {
      console.log('API Endpoint:', report.apiEndpoint);
      console.log('HTTP Status:', report.httpStatus);
    }
    if (report.metadata) {
      console.log('Metadata:', report.metadata);
    }
    if (report.stack) {
      console.log('Stack Trace:', report.stack);
    }
    console.groupEnd();
  }

  private isCriticalError(report: ErrorReport): boolean {
    const criticalErrors = [
      ErrorCode.SERVER_ERROR,
      ErrorCode.NETWORK_ERROR,
      ErrorCode.SERVICE_UNAVAILABLE
    ];
    return criticalErrors.includes(report.errorCode);
  }

  private async sendToAnalytics(errors: ErrorReport[]): Promise<void> {
    if (!this.config.sendToAnalytics) return;

    // Send to Google Analytics, Mixpanel, etc.
    // Example implementation for Google Analytics
    if (typeof (window as any).gtag !== 'undefined') {
      errors.forEach(error => {
        (window as any).gtag('event', 'error', {
          error_code: error.errorCode,
          error_message: error.message,
          component_name: error.componentName,
          user_action: error.userAction,
          api_endpoint: error.apiEndpoint,
          custom_parameter_1: error.environment
        });
      });
    }

    // Example for custom analytics
    try {
      await fetch('/api/analytics/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ errors, sessionId: this.config.sessionId })
      });
    } catch (error) {
      console.warn('Failed to send to analytics:', error);
    }
  }

  private async sendToMonitoring(errors: ErrorReport[]): Promise<void> {
    if (!this.config.sendToMonitoring) return;

    // Send to Sentry, LogRocket, etc.
    // Example for Sentry
    if (typeof (window as any).Sentry !== 'undefined') {
      errors.forEach(error => {
        (window as any).Sentry.captureException(new Error(error.message), {
          tags: {
            errorCode: error.errorCode,
            component: error.componentName,
            environment: error.environment
          },
          extra: {
            userAction: error.userAction,
            apiEndpoint: error.apiEndpoint,
            metadata: error.metadata
          },
          user: {
            id: error.userId,
            session: error.sessionId
          }
        });
      });
    }
  }

  private async sendToBackend(errors: ErrorReport[]): Promise<void> {
    if (!this.config.apiEndpoint || !this.isOnline) return;

    try {
      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey && { 'Authorization': `Bearer ${this.config.apiKey}` })
        },
        body: JSON.stringify({
          errors,
          sessionId: this.config.sessionId,
          environment: this.config.environment,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.warn('Failed to send errors to backend:', error);
      // Could implement retry logic here
    }
  }

  private setupEventListeners(): void {
    // Monitor online/offline status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushQueue(); // Flush queued errors when back online
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Flush errors before page unload
    window.addEventListener('beforeunload', () => {
      if (this.errorQueue.length > 0) {
        // Use sendBeacon for reliable delivery
        const data = JSON.stringify({
          errors: this.errorQueue,
          sessionId: this.config.sessionId
        });
        
        if (this.config.apiEndpoint) {
          navigator.sendBeacon(this.config.apiEndpoint, data);
        }
      }
    });
  }

  private startPeriodicFlush(): void {
    // Flush errors every 30 seconds
    setInterval(() => {
      if (this.errorQueue.length > 0) {
        this.flushQueue();
      }
    }, 30000);
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Singleton instance
export const errorReporting = new ErrorReportingService();

// Convenience functions for common use cases
export const reportError = (error: ApiError | Error, context?: any) => 
  errorReporting.reportError(error, context);

export const reportApiError = (error: ApiError, endpoint: string, status?: number) =>
  errorReporting.reportApiError(error, endpoint, status);

export const reportUserActionError = (error: ApiError | Error, action: string, component: string) =>
  errorReporting.reportUserActionError(error, action, component);

// Configure error reporting
export const configureErrorReporting = (config: Partial<ErrorReportingConfig>) =>
  errorReporting.configure(config);

// Set user context
export const setUserContext = (userId: string, metadata?: Record<string, any>) =>
  errorReporting.setUserContext(userId, metadata);

// Get analytics
export const getErrorAnalytics = () => errorReporting.getErrorAnalytics(); 